import { defineComponent as _defineComponent } from 'vue'
import TableResponsive from "@/components/common/TableResponsive.vue";
import { useWalletStore } from "@/pinia";
import { convertToHumanNumber, formatPrice, ITrades, timeAgo } from "@/shared";
import { PublicKey } from "@solana/web3.js";
import Vue, {
	computed,
	onMounted,
	onUnmounted,
	ref,
	watch,
} from "vue";
import AccountAddress from "@/components/common/AccountAddress.vue";
import AmountEmoji from "@/components/common/AmountEmoji.vue";
import ResizableElement from "../common/ResizableElement.vue";
import PlatformImage from "@/components/common/PlatformImage.vue";
import SignatureAddress from "@/components/common/SignatureAddress.vue";

// props
interface IProps {
	mint: string;
	interval: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TokenTrades',
  props: {
    mint: null,
    interval: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
	mint: string;
	interval: string;
};



// computed
const filterWalletIsInvalid = computed<boolean>(() => {
	try {
		if (!tradeFilterWallet.value) return false;
		return !PublicKey.isOnCurve(
			new PublicKey(tradeFilterWallet.value).toString()
		);
	} catch {
		return true;
	}
});
const getFilteredTrades = computed<ITrades>(() => {
	if (trades.value === null) return null;

	let filteredTrades = [...trades.value];

	if (tradeFilterAmount.value && tradeFilterAmount.value != 0) {
		filteredTrades = filteredTrades.filter((trade) => {
			return (
				Number(trade.price.toFixed(2)) === tradeFilterAmount.value ||
				Number(trade.value.toFixed(2)) === tradeFilterAmount.value
			);
		});
	}

	if (!filterWalletIsInvalid.value && tradeFilterWallet.value.length) {
		filteredTrades = filteredTrades.filter(
			(trade) =>
				trade.inMint === tradeFilterWallet.value ||
				trade.outMint === tradeFilterWallet.value ||
				trade.signer === tradeFilterWallet.value
		);
	}

	if (filterAmountEmoji.value !== null) {
		filteredTrades = filteredTrades.filter((trade) => {
			if (getCurrentEmojiIndex.value === -1) return false;
			if (getCurrentEmojiIndex.value === 0) {
				return trade.value >= 1;
			}

			const prevEmojiAmount =
				emojiAmounts.value[getCurrentEmojiIndex.value - 1];
			return trade.value >= prevEmojiAmount;
		});
	}

	return filteredTrades;
});
const getTradesByValue = computed<(min: number, max: number) => ITrades>(
	() => (min, max) => {
		return (
			trades.value?.filter(
				(trade) => trade.value > min && trade.value <= max
			) ?? null
		);
	}
);
const getCurrentEmojiIndex = computed<number>(() => {
	return emojiAmounts.value.indexOf(filterAmountEmoji.value);
});
const getNumberOfColumns = computed<number>(() => {
	if (viewColumnWidth.value === 0) return 1;
	if (viewColumnWidth.value === null) return 3;
	const num = Math.floor(
		Math.abs(viewColumnWidth.value / MIN_VIEW_COLUMN_WIDTH)
	);
	return num >= 3 ? 3 : num;
});

// store
const walletStore = useWalletStore();

// data
const filterIsVisible = ref<boolean>(false);
const paused = ref<boolean>(false);
const tradeFilterAmount = ref<number>(0);
const trades = ref<ITrades | null>(null);
const tradesOfValue = ref({
	10: [],
	100: [],
	1000: [],
	5000: [],
	10000: [],
	250000: [],
	1000000: [],
	10000000: [],
});
const tradeFilterWallet = ref<string>("");
const workerTrade = new Worker("/js/trade-worker.js");
const filterAmountEmoji = ref<number | null>(null);
const emojiAmounts = ref<number[]>([
	10, 100, 1000, 5000, 10000, 250000, 1000000, 10000000,
]);
const viewType = ref<"table" | "column">("table");
const viewColumnWidth = ref<number | null>(null);
const MIN_VIEW_COLUMN_WIDTH = 420;
const columnsValueRange = ref<number[][]>([
	[1, 10],
	[1000, 5000],
	[10000, 250000],
]);

// methods
function mounted() {
	initTrade(props.mint);
	setWalletIntoWorker();
	watchColumnViewResize();
}
function watchColumnViewResize() {
	const el = document.getElementById("view-column");

	if (!el) {
		setTimeout(watchColumnViewResize, 1000);
		return;
	}

	const observer = new ResizeObserver((entries) => {
		const e = entries[0];

		if (viewColumnWidth.value === e.contentRect.width) return;
		viewColumnWidth.value = e.contentRect.width;
	});

	observer.observe(el);
}

function unmounted() {
	workerTrade.postMessage({ method: "terminate" });
	workerTrade.terminate();
}

function initTrade(mint: string) {
	const throttleTime = 200; // Throttle time in milliseconds
	let messageBuffer = [];
	let processing = false;

	workerTrade.postMessage({ method: "init", data: mint });
	workerTrade.postMessage({method: "interval-change", data: props.interval})

	workerTrade.onmessage = ({ data }) => {
		messageBuffer.push(data.trades);

		if (!processing) {
			processMessages();
		}
	};

	function processMessages() {
		processing = true;

		if (messageBuffer.length > 0) {
			if (!paused.value) {
				const newTrades = messageBuffer[messageBuffer.length - 1]
				trades.value = newTrades;
				for( let i = 0; i < newTrades.length; i++) {
					addTradesByValue(newTrades[i])
				}
			}
			messageBuffer = [];
		}

		setTimeout(() => {
			if (messageBuffer.length > 0) {
				processMessages();
			} else {
				processing = false;
			}
		}, throttleTime);
	}
}


function addTradesByValue(newTrade) {
	if (newTrade.value <= 10) {
		tradesOfValue.value[10].unshift(newTrade)
		if (tradesOfValue.value[10].length > 10) {
			tradesOfValue.value[10].pop()
		}
		return
	} else if (newTrade.value <= 100) {
			tradesOfValue.value[100].unshift(newTrade)
		if (tradesOfValue.value[100].length > 10) {
			tradesOfValue.value[100].pop()
		}
		return
	} else if (newTrade.value <= 1000) {
			tradesOfValue.value[1000].unshift(newTrade)
		if (tradesOfValue.value[1000].length > 10) {
			tradesOfValue.value[1000].pop()
		}
		return
	} else if (newTrade.value <= 5000) {
			tradesOfValue.value[5000].unshift(newTrade)
		if (tradesOfValue.value[5000].length > 10) {
			tradesOfValue.value[5000].pop()
		}
		return
	} else if (newTrade.value <= 10000) {
			tradesOfValue.value[10000].unshift(newTrade)
		if (tradesOfValue.value[10000].length > 10) {
			tradesOfValue.value[10000].pop()
		}
		return
	} else if (newTrade.value <= 250000) {
			tradesOfValue.value[250000].unshift(newTrade)
		if (tradesOfValue.value[250000].length > 10) {
			tradesOfValue.value[250000].pop()
		}
		return
	} else if (newTrade.value <= 1000000) {
			tradesOfValue.value[1000000].unshift(newTrade)
		if (tradesOfValue.value[1000000].length > 10) {
			tradesOfValue.value[1000000].pop()
		}
		return
	} else if (newTrade.value <= 1000000) {
			tradesOfValue.value[1000000].unshift(newTrade)
		if (tradesOfValue.value[1000000].length > 10) {
			tradesOfValue.value[1000000].pop()
		}
		return
	}
}

function setWalletIntoWorker() {
	workerTrade.postMessage({
		method: "wallet",
		data: walletStore.address.toString(),
	});
}

function getTimeOfTransaction(timestamp: number) {
	return timeAgo(timestamp * 1000);
}
function setAmountEmoji(amount: number) {
	filterAmountEmoji.value = amount;
}

function setColumnsValueRange(columnIndex: number, emojiIndex:number) {
	const max = emojiAmounts.value[emojiIndex];
	const min = emojiAmounts.value[emojiIndex - 1] ?? 0;

	Vue.set(columnsValueRange.value, columnIndex, [min, max])
}

//Watch
watch(
	() => props.interval,
	() => {
		console.log("Props Interval change", props.interval)
		workerTrade.postMessage({method: "interval-change", data: props.interval})
	});

// setup
onMounted(mounted);
onUnmounted(unmounted);

// expose
expose({ filterIsVisible, paused, viewType });

return { __sfc: true,props, filterWalletIsInvalid, getFilteredTrades, getTradesByValue, getCurrentEmojiIndex, getNumberOfColumns, walletStore, filterIsVisible, paused, tradeFilterAmount, trades, tradesOfValue, tradeFilterWallet, workerTrade, filterAmountEmoji, emojiAmounts, viewType, viewColumnWidth, MIN_VIEW_COLUMN_WIDTH, columnsValueRange, mounted, watchColumnViewResize, unmounted, initTrade, addTradesByValue, setWalletIntoWorker, getTimeOfTransaction, setAmountEmoji, setColumnsValueRange, TableResponsive, convertToHumanNumber, formatPrice, AccountAddress, AmountEmoji, PlatformImage, SignatureAddress }
}

})